import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type CampanhaPrograma, type RespostaPrograma } from "../api";
import { fmt } from "../endomarketing-utils";
import { IDEIA, SONHO, bannerDoPrograma, programaPorKey, type ProgramaKey } from "../programas";
import {
  IconChat,
  IconCopy,
  IconLink,
  IconPlus,
  IconRefresh,
  IconSparkles,
  IconTrash,
  IconUser,
} from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * PROGRAMAS INTERNOS na aba Endomarketing — é aqui que as respostas VOLTAM.
 *
 * Dois programas, duas abas, listas separadas: "Realize um Sonho" e "Ideias que
 * Transformam" não se misturam em lugar nenhum, porque não são a mesma coisa —
 * um escolhe um sonho por edição, o outro premia melhorias ao longo do ano.
 *
 * O que os dois compartilham é o caminho: o RH abre uma campanha, o sistema
 * gera um LINK, a pessoa responde sem login e a resposta cai aqui identificada
 * pelo NOME. Quando o nome bate com o cadastro, o cartão mostra de quem é
 * no quadro (setor e cargo oficiais) — é o que faz a resposta "voltar de acordo
 * com cada pessoa".
 */

const ABAS: { key: ProgramaKey; label: string }[] = [
  { key: "sonho", label: SONHO.nome },
  { key: "ideia", label: IDEIA.nome },
];

/** Endereço público da campanha, do jeito que a pessoa vai abrir. */
const linkDaCampanha = (token: string) => `${window.location.origin}/programa/${token}`;

/**
 * A mensagem pronta para o WhatsApp. O link vai sozinho na última linha: é o
 * que faz o WhatsApp montar a prévia com o cartaz (server/programa-banner.ts).
 */
function mensagemWhatsApp(programa: ProgramaKey, token: string): string {
  const chamada =
    programa === "sonho"
      ? "✨ *Programa Realize um Sonho* — a SERCAMP acredita em pessoas, e todo mundo carrega um sonho. Conte o seu e participe!"
      : "💡 *Programa Ideias que Transformam* — sua ideia pode melhorar a SERCAMP e ainda valer um prêmio. Participe!";
  return `${chamada}\n\n${linkDaCampanha(token)}`;
}

function situacaoPill(situacao: string): string {
  if (situacao === "Selecionada" || situacao === "Premiada") return "pill pill-ok";
  if (situacao === "Não selecionada") return "pill pill-danger";
  if (situacao === "Em análise") return "pill pill-warn";
  return "pill pill-neutral";
}

/** "1234,5" → "R$ 1.234,50" (o valor do sonho vem NUMERIC do Postgres). */
function reais(v: unknown): string | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : null;
}

/* ------------------------------------------------------------------ *
 *  Um cartão de resposta                                              *
 * ------------------------------------------------------------------ *
 * Fora do componente de propósito (componente aninhado remonta a cada
 * tecla digitada na observação e o campo perderia o foco).             */

function Cartao({
  r,
  programa,
  aberto,
  onAlternar,
  onAvaliar,
  onRemover,
  podeExcluir,
}: {
  r: RespostaPrograma;
  programa: ProgramaKey;
  aberto: boolean;
  onAlternar: () => void;
  onAvaliar: (dados: Record<string, unknown>) => Promise<void>;
  onRemover: () => void;
  podeExcluir: boolean;
}) {
  const prog = programaPorKey(programa);
  const [situacao, setSituacao] = useState(r.situacao);
  const [premio, setPremio] = useState(String(r.premio ?? ""));
  const [observacao, setObservacao] = useState(r.observacao ?? "");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    try {
      await onAvaliar({ situacao, observacao, ...(programa === "ideia" ? { premio } : {}) });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="clima-resposta">
      <header onClick={onAlternar}>
        <div className="prog-quem">
          <strong>{r.nome}</strong>
          <small className="muted">
            {r.setor ? `${r.setor}` : "setor não informado"}
            {r.funcao ? ` · ${r.funcao}` : ""}
            {r.campanha_titulo ? ` · ${r.campanha_titulo}` : ""}
          </small>
          {r.colaborador_id ? (
            <small className="prog-cadastro">
              <IconUser size={12} /> No cadastro: {r.colaborador_nome}
              {r.colaborador_departamento ? ` · ${r.colaborador_departamento}` : ""}
              {r.colaborador_cargo ? ` · ${r.colaborador_cargo}` : ""}
            </small>
          ) : (
            <small className="muted prog-sem-cadastro">
              Este nome não foi encontrado no cadastro de colaboradores.
            </small>
          )}
        </div>
        <div className="prog-etiquetas">
          <span className={situacaoPill(r.situacao)}>{r.situacao}</span>
          {r.premio ? <span className="pill pill-ok">{String(r.premio)}</span> : null}
          <small className="muted">{fmt(r.respondido_em)}</small>
        </div>
      </header>

      {aberto && (
        <div className="clima-resposta-corpo">
          {/* O que a pessoa escreveu — cada programa tem os seus campos. */}
          {prog.campos
            .filter((c) => !["nome", "telefone", "setor", "funcao"].includes(c.nome))
            .map((c) => {
              const valor =
                c.tipo === "currency" ? reais(r[c.nome]) : (r[c.nome] as string | null) || null;
              if (!valor) return null;
              return (
                <div key={c.nome} className="prog-campo">
                  <span className="clima-pergunta-txt">{c.label}</span>
                  <p className="clima-resposta-txt">{valor}</p>
                </div>
              );
            })}

          {r.telefone && (
            <p className="muted prog-contato">Telefone: {r.telefone}</p>
          )}

          {/* A decisão do RH. */}
          <div className="form-grid prog-avaliar">
            <label className="field">
              <span>Situação</span>
              <select value={situacao} onChange={(e) => setSituacao(e.target.value)}>
                {prog.situacoes.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            {programa === "ideia" && (
              <label className="field">
                <span>Premiação</span>
                <select
                  value={premio}
                  onChange={(e) => setPremio(e.target.value)}
                  disabled={situacao !== "Premiada"}
                  title={situacao === "Premiada" ? "" : "Só com a situação 'Premiada'"}
                >
                  <option value="">—</option>
                  {IDEIA.premiacoes.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="field field-wide">
              <span>Observação do RH</span>
              <textarea
                rows={2}
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Fica só aqui — a pessoa não vê."
              />
            </label>
          </div>

          <div className="prog-acoes-cartao">
            <button type="button" className="btn-primary btn-sm" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando…" : "Salvar avaliação"}
            </button>
            {podeExcluir && (
              <button type="button" className="btn-secondary btn-sm btn-danger-text" onClick={onRemover}>
                <IconTrash size={15} /> Excluir
              </button>
            )}
            {r.avaliado_por && (
              <small className="muted">
                Avaliado por {r.avaliado_por}
                {r.avaliado_em ? ` em ${fmt(r.avaliado_em)}` : ""}
              </small>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  A aba de UM programa                                               *
 * ------------------------------------------------------------------ */

function Programa({
  programa,
  onAviso,
}: {
  programa: ProgramaKey;
  onAviso: (m: string) => void;
}) {
  const prog = programaPorKey(programa);
  const podeExcluir = usePodeExcluir();

  const [campanhas, setCampanhas] = useState<CampanhaPrograma[]>([]);
  const [respostas, setRespostas] = useState<RespostaPrograma[]>([]);
  const [filtro, setFiltro] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [abertos, setAbertos] = useState<number[]>([]);
  const [criando, setCriando] = useState(false);
  const [novoTitulo, setNovoTitulo] = useState("");
  const [copiado, setCopiado] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [c, r] = await Promise.all([
        api.programas.campanhas(programa),
        api.programas.respostas(programa, filtro),
      ]);
      setCampanhas(c);
      setRespostas(r);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui carregar o programa.");
    } finally {
      setCarregando(false);
    }
  }, [programa, filtro]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function criarCampanha() {
    const titulo = novoTitulo.trim();
    if (!titulo) return;
    try {
      await api.programas.criarCampanha(programa, { titulo });
      setNovoTitulo("");
      setCriando(false);
      await carregar();
      onAviso(`Campanha "${titulo}" aberta — copie o link e divulgue.`);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui abrir a campanha.");
    }
  }

  async function alternarStatus(c: CampanhaPrograma) {
    const novo = c.status === "Aberta" ? "Encerrada" : "Aberta";
    try {
      await api.programas.atualizarCampanha(c.id, { status: novo });
      await carregar();
      onAviso(
        novo === "Encerrada"
          ? `"${c.titulo}" encerrada — o link deixou de receber respostas.`
          : `"${c.titulo}" reaberta.`,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui mudar a campanha.");
    }
  }

  async function removerCampanha(c: CampanhaPrograma) {
    if (!confirm(`Apagar a campanha "${c.titulo}"? As respostas de quem participou são mantidas.`)) {
      return;
    }
    try {
      const r = await api.programas.removerCampanha(c.id);
      await carregar();
      onAviso(r.mensagem);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui apagar a campanha.");
    }
  }

  async function copiar(c: CampanhaPrograma) {
    const url = linkDaCampanha(c.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(c.id);
      setTimeout(() => setCopiado((v) => (v === c.id ? null : v)), 2500);
    } catch {
      // Sem permissão de área de transferência (acontece em http:// e em alguns
      // navegadores): o link continua visível no campo ao lado para copiar à mão.
      onAviso("Não consegui copiar sozinho — selecione o link e copie.");
    }
  }

  async function avaliar(id: number, dados: Record<string, unknown>) {
    try {
      const r = await api.programas.avaliar(programa, id, dados);
      await carregar();
      onAviso(r.mensagem);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui salvar a avaliação.");
    }
  }

  async function remover(r: RespostaPrograma) {
    if (!confirm(`Excluir a resposta de ${r.nome}? Não dá para desfazer.`)) return;
    try {
      const res = await api.programas.removerResposta(programa, r.id);
      await carregar();
      onAviso(res.mensagem);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui excluir.");
    }
  }

  const resumo = useMemo(() => {
    const total = respostas.length;
    const novas = respostas.filter((r) => r.situacao === "Recebida").length;
    const fechadas = respostas.filter((r) =>
      programa === "sonho" ? r.situacao === "Selecionada" : r.situacao === "Premiada",
    ).length;
    return { total, novas, fechadas };
  }, [respostas, programa]);

  return (
    <div className="prog-area">
      {/* O cartaz do programa: é o banner da página do link e a imagem que
          aparece na prévia quando o link é colado no WhatsApp. */}
      <figure className="prog-rh-banner">
        <a href={bannerDoPrograma(programa)} target="_blank" rel="noreferrer" title="Abrir o cartaz">
          <img src={bannerDoPrograma(programa)} alt={`Cartaz do programa ${prog.nome}`} />
        </a>
        <figcaption>
          <IconSparkles size={13} /> Esta arte aparece em destaque na página do link e na prévia do
          WhatsApp.
        </figcaption>
      </figure>

      <div className="prog-topo">
        <div>
          <h3>{prog.nome}</h3>
          <p className="muted">{prog.chamada}</p>
        </div>
        <div className="prog-topo-acoes">
          <button type="button" className="icon-btn" title="Recarregar" onClick={carregar}>
            <IconRefresh size={16} />
          </button>
          <button type="button" className="btn-primary btn-sm" onClick={() => setCriando((v) => !v)}>
            <IconPlus size={15} /> {programa === "sonho" ? "Nova edição" : "Nova campanha"}
          </button>
        </div>
      </div>

      {criando && (
        <div className="prog-nova">
          <label className="field field-wide">
            <span>{programa === "sonho" ? "Nome da edição" : "Nome da campanha"}</span>
            <input
              autoFocus
              value={novoTitulo}
              onChange={(e) => setNovoTitulo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  criarCampanha();
                }
              }}
              placeholder={
                programa === "sonho" ? "1ª edição — junho/2026" : "Ideias que Transformam — 2026"
              }
            />
          </label>
          <button type="button" className="btn-primary btn-sm" onClick={criarCampanha}>
            Abrir e gerar link
          </button>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setCriando(false)}>
            Cancelar
          </button>
        </div>
      )}

      {erro && <div className="form-error">{erro}</div>}

      {/* ---- As campanhas, cada uma com o seu link ---- */}
      {campanhas.length === 0 ? (
        <div className="empty">
          {carregando
            ? "Carregando…"
            : programa === "sonho"
              ? "Nenhuma edição aberta. Crie a 1ª edição para gerar o link de inscrição."
              : "Nenhuma campanha aberta. Crie uma para gerar o link e o QR Code."}
        </div>
      ) : (
        <div className="prog-campanhas">
          {campanhas.map((c) => (
            <div key={c.id} className="prog-campanha">
              <div className="prog-campanha-topo">
                <strong>{c.titulo}</strong>
                <span className={c.status === "Aberta" ? "pill pill-ok" : "pill pill-neutral"}>
                  {c.status}
                </span>
                <small className="muted">
                  {c.respostas} resposta(s)
                  {c.novas > 0 ? ` · ${c.novas} sem avaliar` : ""}
                  {c.expira_em ? ` · vale até ${fmt(c.expira_em)}` : ""}
                </small>
              </div>

              <div className="link-copy-row">
                <input className="link-copy-input" readOnly value={linkDaCampanha(c.token)} />
                <button type="button" className="btn-secondary btn-sm" onClick={() => copiar(c)}>
                  <IconCopy size={15} /> {copiado === c.id ? "Copiado!" : "Copiar link"}
                </button>
                {c.status === "Aberta" && (
                  <a
                    className="btn-secondary btn-sm"
                    href={`https://wa.me/?text=${encodeURIComponent(mensagemWhatsApp(programa, c.token))}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Abre o WhatsApp com a mensagem e o link — a prévia mostra o cartaz"
                  >
                    <IconChat size={15} /> WhatsApp
                  </a>
                )}
                <a
                  className="btn-secondary btn-sm"
                  href={linkDaCampanha(c.token)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconLink size={15} /> Abrir
                </a>
                <button type="button" className="btn-secondary btn-sm" onClick={() => alternarStatus(c)}>
                  {c.status === "Aberta" ? "Encerrar" : "Reabrir"}
                </button>
                {podeExcluir && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm btn-danger-text"
                    onClick={() => removerCampanha(c)}
                  >
                    <IconTrash size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- As respostas ---- */}
      <div className="prog-respostas-topo">
        <h4>
          Respostas <span className="badge">{resumo.total}</span>
        </h4>
        <div className="prog-resumo">
          {resumo.novas > 0 && <span className="pill pill-warn">{resumo.novas} sem avaliar</span>}
          {resumo.fechadas > 0 && (
            <span className="pill pill-ok">
              {resumo.fechadas} {programa === "sonho" ? "selecionada(s)" : "premiada(s)"}
            </span>
          )}
          <label className="field prog-filtro">
            <select
              value={filtro ?? ""}
              onChange={(e) => setFiltro(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Todas as campanhas</option>
              {campanhas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.titulo}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {respostas.length === 0 ? (
        <div className="empty">
          {carregando ? "Carregando…" : "Ninguém respondeu ainda. Divulgue o link acima."}
        </div>
      ) : (
        <div className="clima-respostas">
          {respostas.map((r) => (
            <Cartao
              key={r.id}
              r={r}
              programa={programa}
              podeExcluir={podeExcluir}
              aberto={abertos.includes(r.id)}
              onAlternar={() =>
                setAbertos((prev) =>
                  prev.includes(r.id) ? prev.filter((x) => x !== r.id) : [...prev, r.id],
                )
              }
              onAvaliar={(dados) => avaliar(r.id, dados)}
              onRemover={() => remover(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  A aba "Programas" do Endomarketing                                 *
 * ------------------------------------------------------------------ */

export default function EndomarketingProgramas({ onAviso }: { onAviso: (m: string) => void }) {
  const [programa, setPrograma] = useState<ProgramaKey>("sonho");

  return (
    <div className="prog">
      {/* Os dois nunca se misturam: cada aba é um programa, com campanha,
          link e respostas próprios. */}
      <nav className="triagem-abas prog-abas">
        {ABAS.map((a) => (
          <button
            key={a.key}
            className={programa === a.key ? "ativa" : ""}
            onClick={() => setPrograma(a.key)}
          >
            {a.label}
          </button>
        ))}
      </nav>

      {/* A key remonta a tela ao trocar de programa — sem isso o estado do
          anterior (campanhas, filtro, cartões abertos) vazaria para o outro. */}
      <Programa key={programa} programa={programa} onAviso={onAviso} />
    </div>
  );
}
